import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Upload, Video, CheckCircle, AlertCircle, 
  Clock, X, Play, StopCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobApply = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    cover_note: '',
    resume_file: null
  });
  
  // Video recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordedVideo, setRecordedVideo] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [videoPreview, setVideoPreview] = useState(null);
  
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  
  const MAX_VIDEO_DURATION = 60; // 60 seconds

  useEffect(() => {
    if (!user) {
      toast.error('Please login to apply');
      navigate(`/jobs/login?redirect=/jobs/${jobId}/apply`);
      return;
    }
    
    // Pre-fill user data
    setFormData(prev => ({
      ...prev,
      full_name: user.full_name || '',
      email: user.email || ''
    }));
    
    fetchJob();
  }, [jobId, user]);

  const fetchJob = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${jobId}`);
      setJob(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Job not found');
      navigate('/jobs/browse');
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: true, 
        audio: true 
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      
      const chunks = [];
      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        setRecordedVideo(blob);
        setVideoPreview(URL.createObjectURL(blob));
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= MAX_VIDEO_DURATION) {
            stopRecording();
            return MAX_VIDEO_DURATION;
          }
          return prev + 1;
        });
      }, 1000);
      
    } catch (error) {
      toast.error('Camera access denied. Please allow camera permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    }
  };

  const deleteRecording = () => {
    setRecordedVideo(null);
    setVideoPreview(null);
    setRecordingTime(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.full_name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!recordedVideo) {
      toast.error('Video cover letter is required');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // In production, upload video to S3 first
      // For now, create application without video URL
      
      const applicationData = {
        job_listing_id: job.id,
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        cover_note: formData.cover_note,
        video_cover_letter_duration_sec: recordingTime,
        video_cover_letter_compressed: true,
        // video_cover_letter_url: videoUrl (will be added after S3 upload)
      };
      
      await axios.post(`${API}/jobs/apply`, applicationData);
      
      toast.success('Application submitted successfully!');
      navigate(`/jobs/${jobId}?applied=true`);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !job) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground mb-2">Apply for Position</h1>
          <p className="text-muted-foreground">{job.title} at {job.employer?.company_name}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Details */}
          <Card>
            <CardHeader>
              <CardTitle>Personal Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  placeholder="John Smith"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="you@example.com"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  placeholder="04XX XXX XXX"
                />
              </div>
            </CardContent>
          </Card>

          {/* Video Cover Letter */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Video className="w-5 h-5 text-purple-600" />
                Video Cover Letter <span className="text-red-500">*</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                <p className="text-sm text-foreground font-medium mb-1">
                  Record a 60-second video cover letter (required)
                </p>
                <p className="text-xs text-muted-foreground">
                  Introduce yourself and explain why you're a great fit. Your video will be automatically compressed to optimize storage.
                </p>
              </div>

              {!recordedVideo && !isRecording && (
                <div>
                  <button
                    type="button"
                    onClick={startRecording}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <Video className="w-5 h-5" />
                    Start Recording
                  </button>
                </div>
              )}

              {isRecording && (
                <div className="space-y-4">
                  <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                    <video 
                      ref={videoRef}
                      autoPlay 
                      muted 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 right-4 flex items-center gap-2 bg-red-500 text-white px-3 py-1 rounded-full">
                      <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                      <span className="text-sm font-semibold">REC</span>
                    </div>
                    <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-1 rounded-full">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span className="font-mono">{recordingTime}s / {MAX_VIDEO_DURATION}s</span>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    type="button"
                    onClick={stopRecording}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <StopCircle className="w-5 h-5" />
                    Stop Recording
                  </button>
                </div>
              )}

              {recordedVideo && videoPreview && (
                <div className="space-y-4">
                  <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                    <video 
                      src={videoPreview}
                      controls
                      className="w-full h-full"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium text-green-800 dark:text-green-200">
                        Video recorded ({recordingTime}s)
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={deleteRecording}
                      className="text-red-600 hover:text-red-700 text-sm font-medium"
                    >
                      Re-record
                    </button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Resume Upload */}
          <Card>
            <CardHeader>
              <CardTitle>Resume (Optional)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-2">
                  Upload your resume (PDF, DOC, DOCX)
                </p>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setFormData({ ...formData, resume_file: e.target.files[0] })}
                  className="hidden"
                  id="resume-upload"
                />
                <label
                  htmlFor="resume-upload"
                  className="inline-block px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg cursor-pointer transition-colors text-sm font-medium"
                >
                  Choose File
                </label>
                {formData.resume_file && (
                  <p className="text-sm text-foreground mt-2">
                    {formData.resume_file.name}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Cover Note */}
          <Card>
            <CardHeader>
              <CardTitle>Cover Note (Optional)</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={formData.cover_note}
                onChange={(e) => setFormData({ ...formData, cover_note: e.target.value })}
                placeholder="Tell us why you're interested in this role..."
                rows={5}
              />
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate(`/jobs/${jobId}`)}
              className="flex-1 px-6 py-3 border border-border rounded-lg hover:bg-muted transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !recordedVideo}
              className="flex-1 px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : 'Submit Application'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobApply;
